import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * Checkout: el botón "Finalizar compra" en el drawer es un <a href="/checkout"> (snippet cart-summary con in_drawer).
 * Este handler en captura prioriza la navegación al checkout: evita que otros listeners (analytics, etc.)
 * hagan preventDefault/stopPropagation en el primer clic y garantiza un solo clic para ir a checkout.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener('click', this.#handleCheckoutClickCapture, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener('click', this.#handleCheckoutClickCapture, true);
  }

  /**
   * Prioridad navegación: clic en el enlace de checkout del drawer → ir a checkout de inmediato.
   * En captura para ejecutar antes que analytics u otros scripts que puedan hacer preventDefault.
   * @param {MouseEvent} event
   */
  #handleCheckoutClickCapture = (event) => {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog?.open) return;

    const target = event.target instanceof Element ? event.target : null;
    const link = target?.closest?.('.cart-drawer__summary a.cart__checkout-button');
    if (!link || !(link instanceof HTMLAnchorElement) || link.getAttribute('aria-disabled') === 'true') return;

    const href = link.getAttribute('href');
    if (!href || href === '#' || href.startsWith('javascript:')) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    window.location.href = href;
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();
    
    // Limpiar links de WhatsApp cuando se abre el drawer
    setTimeout(() => this.#cleanWhatsAppLinks(), 100);

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
    
    // Limpiar links de WhatsApp después de actualizar el estado
    this.#cleanWhatsAppLinks();
  }

  /**
   * Limpia cualquier link de WhatsApp de los botones del carrito drawer.
   */
  #cleanWhatsAppLinks() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Limpiar botones del drawer del carrito (no tocar el enlace de checkout /checkout)
    const cartButtons = dialog.querySelectorAll('button, a.button, .cart__checkout-button, .cart__view-cart-button, .cart-items__remove, .quantity-minus, .quantity-plus, button[form="cart-form"]');
    cartButtons.forEach(button => {
      if (button.classList.contains('cart__checkout-button')) {
        const href = button.getAttribute('href');
        if (href && (href === '/checkout' || href.endsWith('/checkout'))) return;
      }
      // Eliminar href de WhatsApp
      if (button.hasAttribute('href')) {
        const href = button.getAttribute('href');
        if (href && (href.includes('whatsapp') || href.includes('wa.me'))) {
          button.removeAttribute('href');
        }
      }
      // Eliminar onclick de WhatsApp
      if (button.hasAttribute('onclick')) {
        const onclick = button.getAttribute('onclick');
        if (onclick && (onclick.includes('whatsapp') || onclick.includes('wa.me'))) {
          button.removeAttribute('onclick');
        }
      }
      // Eliminar data attributes de WhatsApp
      button.removeAttribute('data-whatsapp');
      button.removeAttribute('data-wa-link');
      button.removeAttribute('data-href');
      
      // Eliminar links de WhatsApp dentro de los botones
      const whatsappLinks = button.querySelectorAll('a[href*="whatsapp"], a[href*="wa.me"]');
      whatsappLinks.forEach(link => {
        link.removeAttribute('href');
        link.style.display = 'none';
        link.style.visibility = 'hidden';
        link.remove();
      });
    });

    // Limpiar selectores de cantidad
    const quantitySelectors = dialog.querySelectorAll('.quantity-selector, quantity-selector-component, cart-quantity-selector-component');
    quantitySelectors.forEach(selector => {
      const whatsappLinks = selector.querySelectorAll('a[href*="whatsapp"], a[href*="wa.me"]');
      whatsappLinks.forEach(link => {
        link.removeAttribute('href');
        link.style.display = 'none';
        link.style.visibility = 'hidden';
        link.remove();
      });
      
      // Limpiar botones dentro del selector
      const buttons = selector.querySelectorAll('button');
      buttons.forEach(button => {
        if (button.hasAttribute('href')) {
          const href = button.getAttribute('href');
          if (href && (href.includes('whatsapp') || href.includes('wa.me'))) {
            button.removeAttribute('href');
          }
        }
        button.removeAttribute('data-whatsapp');
        button.removeAttribute('data-wa-link');
      });
    });

    // Limpiar formularios del carrito
    const cartForms = dialog.querySelectorAll('form#cart-form, form.cart-form');
    cartForms.forEach(form => {
      if (form.hasAttribute('action')) {
        const action = form.getAttribute('action');
        if (action && (action.includes('whatsapp') || action.includes('wa.me'))) {
          form.removeAttribute('action');
        }
      }
    });
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
